import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Plus, Search, Save, Archive, ArchiveRestore, Pencil, Armchair,
  Lock,
} from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useBranch } from "@/context/BranchContext";
import {
  CHAIR_CAPABILITIES,
  getChairCapabilityMeta,
  normalizeChairCapabilities,
} from "@/lib/chairCapabilities";
import SidePanel from "@/components/calendar/SidePanel";

// =====================================================================
// Settings → Chairs (V2 Round 4 follow-on)
//
// Manages the clinic's chairs/operatories — the columns the calendar
// renders. Same shape as Services but trivially smaller (one field:
// name).
//
// Calls:
//   list_chairs_for_settings(p_include_archived)
//   create_chair / update_chair
//   archive_chair / unarchive_chair
//
// No hard delete: chairs.id is referenced ON DELETE RESTRICT from
// appointments, so a real DELETE on a chair with history would fail.
// Archive is the soft-delete path — keeps historical bookings happy
// while removing the chair from booking dropdowns.
// =====================================================================

export default function SettingsChairs() {
  const { activeBranch, activeBranchId } = useBranch();
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [rows, setRows]             = useState([]);
  const [search, setSearch]         = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [panel, setPanel]           = useState(null); // null | { mode:'new' } | { mode:'edit', row }

  const load = useCallback(async () => {
    if (!activeBranchId) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true); setError(null);
    const { data, error: e } = await supabase.rpc("list_chairs_for_settings", {
      p_branch_id: activeBranchId,
      p_include_archived: showArchived,
    });
    setLoading(false);
    if (e) { setError(e.message ?? "Could not load chairs."); return; }
    setRows(data ?? []);
  }, [activeBranchId, showArchived]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const capabilityText = normalizeChairCapabilities(r.chair_capabilities)
        .map((key) => getChairCapabilityMeta(key)?.label || key)
        .join(" ");
      return `${r.name || ""} ${capabilityText}`.toLowerCase().includes(q);
    });
  }, [rows, search]);

  return (
    <div className="space-y-4" data-testid="settings-chairs" data-tutorial-id="settings-chairs">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm px-5 py-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Chairs</h1>
            <p className="text-xs text-slate-500">
              {activeBranch?.name ? `${activeBranch.name} chair setup.` : "Select an active branch to manage chairs."}
            </p>
          </div>
          <button
            onClick={() => setPanel({ mode: "new" })}
            data-testid="chairs-new-button"
            disabled={!activeBranchId}
            data-tutorial-id="chairs-new-button"
            className="px-4 py-2 text-sm font-semibold bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-40 inline-flex items-center gap-1.5"
          >
            <Plus className="w-4 h-4" /> Add chair
          </button>
        </div>

        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
            <input
              type="text" value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name…"
              data-testid="chairs-search"
              className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded focus:outline-none focus:border-teal-500"
            />
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox" checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              data-testid="chairs-show-archived"
              className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-300"
            />
            Show archived
          </label>
        </div>
      </div>

      {error ? (
        <div role="alert" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="bg-white rounded-2xl shadow-sm p-8 text-sm text-slate-500 flex items-center gap-2" data-tutorial-id="settings-chairs-list">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm p-12 text-center" data-testid="chairs-empty-state" data-tutorial-id="settings-chairs-list">
          <p className="text-sm font-semibold text-slate-700">
            {search.trim() ? "No chairs match your search." : "No chairs yet"}
          </p>
          {!search.trim() ? (
            <>
              <p className="text-xs text-slate-500 mt-1 mb-4">
                Add at least one chair so appointments have somewhere to live on the calendar.
              </p>
              <button
                onClick={() => setPanel({ mode: "new" })}
                disabled={!activeBranchId}
                className="px-4 py-2 text-sm font-semibold bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-40 inline-flex items-center gap-1.5"
              >
                <Plus className="w-4 h-4" /> Add your first chair
              </button>
            </>
          ) : null}
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden" data-tutorial-id="settings-chairs-list">
          <ul className="divide-y divide-slate-100">
            {filtered.map((r) => (
              <li key={r.id}>
                <button
                  onClick={() => setPanel({ mode: "edit", row: r })}
                  data-testid={`chairs-row-${r.id}`}
                  className={[
                    "w-full text-left px-5 py-3 hover:bg-slate-50 flex items-center gap-3",
                    r.archived ? "opacity-50" : "",
                  ].join(" ")}
                >
                  <Armchair className="w-4 h-4 text-slate-400 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-slate-900 truncate">{r.name}</p>
                      {r.archived ? (
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-50 text-rose-600 font-semibold">
                          archived
                        </span>
                      ) : null}
                      {r.available_for_auto_booking === false ? (
                        <span
                          data-testid={`chairs-row-restricted-${r.id}`}
                          className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 font-semibold inline-flex items-center gap-1"
                          title="This chair is not auto-assigned by Recall — front desk must pick it manually from Calendar."
                        >
                          <Lock className="w-3 h-3" /> Manual only
                        </span>
                      ) : null}
                      {normalizeChairCapabilities(r.chair_capabilities).map((key) => {
                        const meta = getChairCapabilityMeta(key);
                        return (
                          <span
                            key={key}
                            data-testid={`chairs-row-capability-${r.id}-${key}`}
                            className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 font-semibold"
                          >
                            {meta?.shortLabel || key}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  <Pencil className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Slide-in form panel */}
      <SidePanel
        open={!!panel}
        onClose={() => setPanel(null)}
        title={panel?.mode === "edit" ? "Edit chair" : "Add chair"}
      >
        {panel ? (
          <ChairForm
            row={panel.mode === "edit" ? panel.row : null}
            branchId={activeBranchId}
            onSaved={() => { setPanel(null); load(); }}
            onCancel={() => setPanel(null)}
          />
        ) : null}
      </SidePanel>
    </div>
  );
}

// ---------------------------------------------------------------------
// ChairForm — used for both create and edit
// ---------------------------------------------------------------------
function ChairForm({ row, branchId, onSaved, onCancel }) {
  const isEdit = !!row;
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState(null);
  const [name, setName]   = useState(row?.name || "");
  const [autoBook, setAutoBook] = useState(
    row?.available_for_auto_booking !== false
  );
  const [capabilities, setCapabilities] = useState(() =>
    normalizeChairCapabilities(row?.chair_capabilities)
  );

  const canSave = name.trim().length > 0 && !!branchId;

  function toggleCapability(key) {
    setCapabilities((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return CHAIR_CAPABILITIES
        .map((item) => item.key)
        .filter((candidate) => next.has(candidate));
    });
  }

  async function handleSave() {
    setBusy(true); setError(null);
    const payload = {
      name: name.trim(),
      branch_id: row?.branch_id || branchId,
      chair_capabilities: capabilities,
    };
    if (isEdit) {
      payload.id = row.id;
      payload.available_for_auto_booking = autoBook;
    }
    const rpc = isEdit ? "update_chair" : "create_chair";
    const { error: e } = await supabase.rpc(rpc, { p_input: payload });
    setBusy(false);
    if (e) { setError(e.message ?? "Could not save."); return; }
    toast.success(isEdit ? "Chair updated." : "Chair added.");
    onSaved?.();
  }

  async function handleArchiveToggle() {
    if (!isEdit) return;
    setBusy(true); setError(null);
    const rpc = row.archived ? "unarchive_chair" : "archive_chair";
    const { error: e } = await supabase.rpc(rpc, { p_id: row.id });
    setBusy(false);
    if (e) { setError(e.message ?? "Could not change archive state."); return; }
    toast.success(row.archived ? "Chair restored." : "Chair archived.");
    onSaved?.();
  }

  return (
    <div className="flex flex-col h-full" data-tutorial-id="settings-chair-form">
      <div className="flex-1 overflow-y-auto p-5 space-y-3">
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1">
            Name <span className="text-rose-500">*</span>
          </label>
          <input
            type="text" value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Main, Chair 1, Pedo Room"
            autoFocus
            data-testid="chair-name"
            className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-teal-500"
          />
          <p className="text-[11px] text-slate-500 mt-1">Shown as a column header on the calendar.</p>
        </div>

        <div className="border-t border-slate-100 pt-3">
          <p className="block text-xs font-semibold text-slate-700 mb-2">Chair capabilities</p>
          <div className="space-y-2">
            {CHAIR_CAPABILITIES.map((capability) => {
              const selected = capabilities.includes(capability.key);
              return (
                <label
                  key={capability.key}
                  className={[
                    "flex items-start gap-3 rounded-lg border px-3 py-2 cursor-pointer",
                    selected ? "border-teal-200 bg-teal-50" : "border-slate-200 bg-white hover:bg-slate-50",
                  ].join(" ")}
                  data-testid={`chair-capability-${capability.key}`}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => toggleCapability(capability.key)}
                    className="mt-0.5 w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-300"
                  />
                  <span className="min-w-0">
                    <span className="block text-xs font-semibold text-slate-800">{capability.label}</span>
                    <span className="block text-[11px] leading-relaxed text-slate-500">{capability.description}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        {isEdit ? (
          <div className="border-t border-slate-100 pt-3">
            <label className="flex items-start gap-3 cursor-pointer" data-testid="chair-auto-book-toggle">
              <input
                type="checkbox"
                checked={autoBook}
                onChange={(e) => setAutoBook(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-300"
              />
              <span className="flex-1">
                <span className="block text-xs font-semibold text-slate-700">Available for auto-booking</span>
                <span className="block text-[11px] text-slate-500 mt-0.5 leading-relaxed">
                  When ON, the Recall Center can automatically pick this chair for new bookings. Turn OFF for restricted chairs (e.g. pedia-only, surgery-only) — front desk will still be able to book into it manually from Calendar.
                </span>
              </span>
            </label>
          </div>
        ) : null}

        {error ? (
          <div role="alert" className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
            {error}
          </div>
        ) : null}
      </div>

      {/* Sticky footer */}
      <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-slate-100 bg-white">
        {isEdit ? (
          <button
            onClick={handleArchiveToggle} disabled={busy}
            data-testid="chair-archive-toggle"
            className="px-3 py-2 text-xs border border-slate-200 rounded text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1.5"
          >
            {row.archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
            {row.archived ? "Restore" : "Archive"}
          </button>
        ) : <span />}
        <div className="flex items-center gap-2">
          <button
            onClick={onCancel} disabled={busy}
            className="px-4 py-2 text-sm border border-slate-200 rounded text-slate-600 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave} disabled={busy || !canSave}
            data-testid="chair-save"
            className="px-5 py-2 text-sm font-semibold bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-40 inline-flex items-center gap-1.5"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isEdit ? "Save changes" : "Add chair"}
          </button>
        </div>
      </div>
    </div>
  );
}
